import useScrollFade from '../../../hooks/useScrollFade';

const services = [
  {
    iconFill: 'ri-user-star-fill',
    iconLine: 'ri-user-star-line',
    border: 'border-teal-400/30',
    iconColor: 'text-teal-600',
    iconBg: 'bg-teal-50',
    hoverIconBg: 'group-hover:bg-teal-50',
    hoverBorder: 'group-hover:border-teal-300',
    hoverShadow: 'hover:shadow-teal-100/60',
    topBar: 'via-teal-400',
    title: 'Fractional CTO Leadership',
    description: 'Provide executive-level technical leadership on a part-time basis.',
  },
  {
    iconFill: 'ri-compass-3-fill',
    iconLine: 'ri-compass-3-line',
    border: 'border-amber-400/30',
    iconColor: 'text-amber-600',
    iconBg: 'bg-amber-50',
    hoverIconBg: 'group-hover:bg-amber-50',
    hoverBorder: 'group-hover:border-amber-300',
    hoverShadow: 'hover:shadow-amber-100/60',
    topBar: 'via-amber-400',
    title: 'Technology Strategy & Architecture',
    description: 'Design scalable software systems and long-term technical roadmaps.',
  },
  {
    iconFill: 'ri-lifebuoy-fill',
    iconLine: 'ri-lifebuoy-line',
    border: 'border-rose-400/30',
    iconColor: 'text-rose-600',
    iconBg: 'bg-rose-50',
    hoverIconBg: 'group-hover:bg-rose-50',
    hoverBorder: 'group-hover:border-rose-300',
    hoverShadow: 'hover:shadow-rose-100/60',
    topBar: 'via-rose-400',
    title: 'Software Project Rescue',
    description: 'Diagnose and stabilize troubled development projects.',
  },
  {
    iconFill: 'ri-team-fill',
    iconLine: 'ri-team-line',
    border: 'border-emerald-400/30',
    iconColor: 'text-emerald-600',
    iconBg: 'bg-emerald-50',
    hoverIconBg: 'group-hover:bg-emerald-50',
    hoverBorder: 'group-hover:border-emerald-300',
    hoverShadow: 'hover:shadow-emerald-100/60',
    topBar: 'via-emerald-400',
    title: 'Engineering Team Leadership',
    description: 'Mentor developers and implement strong engineering processes.',
  },
  {
    iconFill: 'ri-speed-fill',
    iconLine: 'ri-speed-line',
    border: 'border-orange-400/30',
    iconColor: 'text-orange-600',
    iconBg: 'bg-orange-50',
    hoverIconBg: 'group-hover:bg-orange-50',
    hoverBorder: 'group-hover:border-orange-300',
    hoverShadow: 'hover:shadow-orange-100/60',
    topBar: 'via-orange-400',
    title: 'System & Process Optimization',
    description: 'Identify bottlenecks and tune systems, workflows, and delivery pipelines for peak performance and efficiency.',
  },
  {
    iconFill: 'ri-refresh-fill',
    iconLine: 'ri-refresh-line',
    border: 'border-indigo-400/30',
    iconColor: 'text-indigo-600',
    iconBg: 'bg-indigo-50',
    hoverIconBg: 'group-hover:bg-indigo-50',
    hoverBorder: 'group-hover:border-indigo-300',
    hoverShadow: 'hover:shadow-indigo-100/60',
    topBar: 'via-indigo-400',
    title: 'Legacy System Modernization',
    description: 'Transform outdated software into scalable modern platforms.',
  },
];

const ServicesSection = () => {
  const header = useScrollFade();
  const card0 = useScrollFade({ delay: 0 });
  const card1 = useScrollFade({ delay: 80 });
  const card2 = useScrollFade({ delay: 160 });
  const card3 = useScrollFade({ delay: 240 });
  const card4 = useScrollFade({ delay: 320 });
  const card5 = useScrollFade({ delay: 400 });

  const cardRefs = [card0, card1, card2, card3, card4, card5];

  return (
    <section id="services" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          ref={header.ref}
          className={`text-center mb-16 ${header.fadeClass}`}
        >
          <h3 className="text-teal-600 text-sm font-bold uppercase tracking-wider mb-3">
            Service Outcomes
          </h3>
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
            How We Help Companies Succeed Technically
          </h2>
          <div className="flex items-center justify-center gap-2 mt-6">
            <div className="h-px w-16 bg-gray-200" />
            <div className="h-0.5 w-10 rounded-full bg-teal-400 shadow-[0_0_8px_rgba(20,184,166,0.6)]" />
            <div className="h-px w-16 bg-gray-200" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const { ref, fadeClass } = cardRefs[index];
            return (
              <div
                key={index}
                ref={ref}
                className={`bg-white rounded-xl p-8 border border-gray-100 shadow-sm hover:shadow-xl ${service.hoverShadow} hover:-translate-y-1 group relative overflow-hidden transition-all duration-300 ${fadeClass}`}
              >
                <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent ${service.topBar} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

                <div className={`w-14 h-14 flex items-center justify-center rounded-2xl ${service.iconBg} border ${service.border} mb-6 group-hover:scale-110 ${service.hoverIconBg} ${service.hoverBorder} transition-all duration-300 relative`}>
                  <i className={`${service.iconLine} text-2xl ${service.iconColor} relative z-10 transition-colors duration-300`}></i>
                </div>

                <span className="absolute top-6 right-6 text-5xl font-black text-gray-50 select-none leading-none">
                  {String(index + 1).padStart(2, '0')}
                </span>

                <h4 className="text-lg font-bold text-gray-900 mb-3 relative">{service.title}</h4>
                <p className="text-gray-500 text-sm leading-relaxed relative">{service.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;